<template>
    <div
        class="match-info-score JARVIS_MatchCard_Img_Score"
        v-bind:class="[sportName]"
    >
        <div
            class="live-display-time notranslate"
            v-bind:class="{ liveGroup: isShowTournamentCenterMatchInfo }"
        >
            <div v-if="isInMatchDetail" class="match-info-tag_live">
                {{ getTranslationBySite('live').toUpperCase() }}
            </div>

            <time v-if="displayLiveScoreData?.liveScoreInfoDetail" v-bind:class="{ 'tournament-center-time': isShowTournamentCenterMatchInfo }">{{ displayLiveScoreData.liveScoreInfoDetail }}</time>

            <Icons
                v-if="isShowTournamentCenterMatchInfo && showLiveStreamGifIcon"
                class="iconsSize-16 liveTv"
                iconName="systemIcon_tvPlay"
            />

            <img
                v-else-if="showLiveStreamGifIcon"
                src="@/assets/images/liveVideo.gif"
                alt=""
            >
        </div>
        <p
            v-if="displayLiveScoreData"
            class="match-info-score-text"
            v-bind:class="cricketRunningTeamClass"
        >
            <span class="home-score">{{ displayLiveScoreData.homeScore }}</span> : <span class="away-score">{{ displayLiveScoreData.awayScore }}</span>
        </p>
    </div>
</template>

<script lang="ts" setup>
    import type { PropType } from 'vue';
    import { computed, ref, toRefs, watch } from 'vue';
    import useMatchInfoScore from '@/components/match/matchInfo/useMatchInfoScore';
    import type { IMatch } from '@/interface/IMatch';
    import { LiveScoreProvider } from '@/interface/enum';
    import { isShowTournamentCenterMatchInfo as _isShowTournamentCenterMatchInfo } from '@/libraries/composables/useZone';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { getSportTypeKey } from '@/libraries/helpers/sportsHelper';
    import Icons from '@/svg/Icons.vue';

    const props = defineProps({
        matchInfo: {
            type: Object as PropType<IMatch>,
            required: true,
        },
        isInMatchDetail: {
            type: Boolean,
            required: true,
        },
        showLiveStreamGifIcon: {
            type: Boolean,
            required: true,
        },
    });

    const { matchInfo, isInMatchDetail, showLiveStreamGifIcon } = toRefs(props);

    const {
        displayLiveScoreData,
        liveScoreInfo,
    } = useMatchInfoScore(matchInfo);
    const sportName = computed(() => getSportTypeKey(matchInfo.value?.sportType).toLowerCase());

    const cricketRunningTeamClass = ref('running-team-none');

    const isShowTournamentCenterMatchInfo = computed(() => _isShowTournamentCenterMatchInfo(matchInfo.value.leagueName));

    watch(liveScoreInfo, (newValue, oldValue) => {
        if (displayLiveScoreData.value?.scoreProvider !== LiveScoreProvider.Cricket || !newValue?.cricket || !oldValue?.cricket) {
            cricketRunningTeamClass.value = 'running-team-none';
            return;
        }
        const newCricket = newValue.cricket;
        const oldCricket = oldValue.cricket;
        if (newCricket.homeScore > oldCricket.homeScore || newCricket.homeDismissals > oldCricket.homeDismissals) {
            cricketRunningTeamClass.value = 'running-team-home';
        } else if (newCricket.awayScore > oldCricket.awayScore || newCricket.awayDismissals > oldCricket.awayDismissals) {
            cricketRunningTeamClass.value = 'running-team-away';
        } else {
            cricketRunningTeamClass.value = 'running-team-none';
        }
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/tournamentMatchInfo";

.match-info-tag_live {
    padding: 0.3rem 0.5rem;
    margin: 0 auto 4px;
    background-image: $live-bg;
    border-radius: 0.3rem;
    color: white;
    font-size: 1rem;
    font-style: italic;
}

.match-info-score {
    margin: 0 1rem;
    font-size: 1.8em;
    font-weight: bolder;
    text-align: center;

    .live-display-time {
        line-height: 1rem;
    }

    time:not(.tournament-center-time) {
        display: inline-block;
        margin: 0 4px 2px;
        color: $gray-1;
        font-size: 1rem;
        line-height: 0.8rem;
        transform: translateY(-2px);
    }

    .tournament-center-time {
        margin-right: 4px;
        font-size: 10px;
        font-weight: 700;
    }

    img {
        margin: 0 3px;
    }

    .match-info-score-text {
        font-size: 100%;
    }

    &.cricket {
        .match-info-score-text {
            font-size: 1.5rem;
        }

        .running-team-home {
            .home-score {
                color: #ff760f;
            }
        }

        .running-team-away {
            .away-score {
                color: #ff760f;
            }
        }
    }
}
</style>
